import {get, post, put} from '@/services/infrastructure/api.service'
import {AssignBankAccountCommand, BankAccount, CreateBankAccountCommand, RichBankAccount, UpdateBankAccountCommand} from '@/bank/bank.types'
import {ref} from 'vue'
import emitter from '@/services/infrastructure/emitter'
import {LOGOUT, NO_ORG_SELECTED, ORG_SELECTED, ORG_SWITCHED} from '@/types/events'

const BANK_ACCOUNT_PATH = '/bank-accounts'
const BANK_ACCOUNT_CREATED = 'bankAccountCreated'
const BANK_ACCOUNT_UPDATED = 'bankAccountUpdated'
export const bankAccounts = ref<RichBankAccount[]>([])

const fetchBankAccounts = async () => {
    bankAccounts.value = await get<RichBankAccount[]>(BANK_ACCOUNT_PATH) ?? []
}

const createBankAccount = async (cmd: CreateBankAccountCommand): Promise<BankAccount | undefined> => {
    return await post(BANK_ACCOUNT_PATH, cmd)
}

const updateBankAccount = async (cmd: UpdateBankAccountCommand): Promise<BankAccount | undefined> => {
    return await put(BANK_ACCOUNT_PATH, cmd)
}

const assignBankAccount = async (cmd: AssignBankAccountCommand): Promise<RichBankAccount | undefined> => {
    return await post(BANK_ACCOUNT_PATH + '/:assign', cmd)
}

const validateBban = (bankAccountNo: string): boolean => {
    if (bankAccountNo.length !== 11) return false

    const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2]
    const sum = bankAccountNo
        .substring(0, 10)
        .split('')
        .reduce((acc, digit, index) => acc + parseInt(digit, 10) * weights[index], 0)

    const remainder = sum % 11
    const controlDigit = parseInt(bankAccountNo.charAt(10), 10)
    return controlDigit === (remainder === 0 ? 0 : 11 - remainder)
}

const sanitizeBban = (bban: string): string => {
    return bban.replace(/\D/g, '')
}

const validateIban = (iban: string): boolean => BigInt([...iban.slice(4), ...iban.slice(0, 4)]
    .map(c => /[a-z]/i.test(c) ? c.toLowerCase().charCodeAt(0) - 87 : c)
    .join('')) % 97n === 1n

const validateBic = (bic: string): boolean => {
    const regex = new RegExp('^[a-zA-Z]{6}[a-zA-Z0-9]{2}([a-zA-Z0-9]{3})?$', '')
    return regex.test(bic)
}

const formatBankAccountNo = (bankAccountNo: string): string => {
    if (bankAccountNo.length !== 11) return bankAccountNo
    return bankAccountNo.substring(0, 4) + ' ' + bankAccountNo.substring(4, 6) + ' ' + bankAccountNo.substring(6)
}

const accountNoIsValidBankRange = (accountNo: string) => {
    return accountNo.match(/^[12][0-9]{3}$/)
}

const findBankAccountByAccountNo = (accountNo: string): RichBankAccount | undefined => {
    return bankAccounts.value.find(ba => ba.bookkeepingAccountNo === accountNo)
}

const clear = () => {
    bankAccounts.value = []
}

emitter.on(BANK_ACCOUNT_UPDATED, fetchBankAccounts)
emitter.on(BANK_ACCOUNT_CREATED, fetchBankAccounts)
emitter.on(ORG_SWITCHED, fetchBankAccounts)
emitter.on(ORG_SELECTED, fetchBankAccounts)
emitter.on(NO_ORG_SELECTED, clear)
emitter.on(LOGOUT, clear)

export {
    fetchBankAccounts,
    createBankAccount,
    updateBankAccount,
    assignBankAccount,
    validateBban,
    sanitizeBban,
    formatBankAccountNo,
    accountNoIsValidBankRange,
    findBankAccountByAccountNo,
    validateIban,
    validateBic
}